// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/EntityCombobox.tsx
//
//  Combobox genérico para escolher uma entidade SEM pedir ID:
//    • busca remota com debounce (código, nome, documento — quem decide é a API);
//    • rótulo "Código — Nome" e linha extra (CPF/CNPJ, cidade…);
//    • mostra o valor salvo mesmo fora dos primeiros N (busca por GET /{id});
//    • onSelect devolve o objeto inteiro (autopreencher campos dependentes).
//
//  Os comboboxes específicos são wrappers finos dele (ver FornecedorCombobox).
//  Endpoint da convenção: GET .../lookup?busca=&limite= → LookupItem[].
// ════════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce";
import { rotuloCodigoNome } from "@/lib/consultas";

export interface EntityComboboxProps<T> {
  /** Id selecionado (0/null/undefined = nada). */
  value?: number | null;
  /** Só o id. Use `onSelect` quando precisar do objeto. */
  onChange?: (id: number | null) => void;
  /** Objeto inteiro escolhido (null ao limpar). */
  onSelect?: (item: T | null) => void;

  /** Base da chave de cache do React Query (ex.: "fornecedor-lookup"). */
  queryKey: string;
  /** Busca remota. Recebe o termo já aparado ("" = primeiros itens). */
  buscar: (termo: string) => Promise<T[]>;
  /** Carrega o item salvo para exibir o rótulo (GET /{id}). */
  obterPorId?: (id: number) => Promise<T>;
  /**
   * Item já conhecido pela tela (ex.: id + nome vindos do DTO em edição). Quando o id
   * bate com `value`, é exibido sem chamar `obterPorId`.
   */
  itemInicial?: T | null;

  getId: (item: T) => number;
  getDescricao: (item: T) => string;
  getCodigo?: (item: T) => string | number | null | undefined;
  getExtra?: (item: T) => string | null | undefined;

  /** Caracteres mínimos para buscar (0 = lista os primeiros ao abrir). */
  minCaracteres?: number;
  debounceMs?: number;
  placeholder?: string;
  placeholderBusca?: string;
  /** Mostra o "x" para limpar a seleção. */
  permitirLimpar?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

export function EntityCombobox<T>({
  value, onChange, onSelect, queryKey, buscar, obterPorId, itemInicial,
  getId, getDescricao, getCodigo, getExtra,
  minCaracteres = 0, debounceMs = 300,
  placeholder = "Selecione...", placeholderBusca = "Buscar por código ou nome...",
  permitirLimpar = true, disabled, className, id, "aria-label": ariaLabel,
}: EntityComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // O item escolhido nesta sessão já tem o rótulo: evita esperar o GET /{id}.
  const [escolhido, setEscolhido] = useState<T | null>(null);
  const termo = useDebounce(search.trim(), debounceMs);
  const podeBuscar = termo.length >= minCaracteres;

  const { data: resultados = [], isFetching } = useQuery({
    queryKey: [queryKey, "busca", termo],
    queryFn: () => buscar(termo),
    enabled: open && podeBuscar,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });

  const temValor = !!value;
  const conhecido = [escolhido, itemInicial].find((i) => i && temValor && getId(i) === value) ?? null;
  const { data: salvo, isLoading: carregandoSalvo, isError: salvoNaoEncontrado } = useQuery({
    queryKey: [queryKey, "id", value],
    queryFn: () => obterPorId!(value!),
    enabled: temValor && !conhecido && !!obterPorId,
    staleTime: 60_000,
    retry: false,
  });
  const selecionado = conhecido ?? salvo ?? null;

  const rotulo = (item: T) => rotuloCodigoNome(getCodigo?.(item), getDescricao(item));

  const escolher = (item: T | null) => {
    setEscolhido(item);
    onChange?.(item ? getId(item) : null);
    onSelect?.(item);
    setOpen(false);
    setSearch("");
  };

  const textoBotao = selecionado
    ? rotulo(selecionado)
    : temValor && carregandoSalvo ? "Carregando..."
    : temValor && salvoNaoEncontrado ? "(registro não encontrado)"
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn("relative w-full", className)}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            className={cn("w-full justify-between font-normal", permitirLimpar && selecionado && "pr-14")}
            disabled={disabled}
            type="button"
          >
            <span className={cn("truncate", !selecionado && "text-muted-foreground")}>{textoBotao}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {permitirLimpar && selecionado && !disabled && (
          <button
            type="button"
            aria-label="Limpar seleção"
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            onClick={() => escolher(null)}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-[min(450px,90vw)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholderBusca} value={search} onValueChange={setSearch} />
          <CommandList>
            {isFetching && resultados.length === 0 ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <CommandEmpty>
                {!podeBuscar
                  ? `Digite ao menos ${minCaracteres} caractere${minCaracteres > 1 ? "s" : ""}...`
                  : "Nenhum resultado encontrado."}
              </CommandEmpty>
            )}
            {podeBuscar && resultados.length > 0 && (
              <CommandGroup className={cn(isFetching && "opacity-60")}>
                {resultados.map((item) => {
                  const itemId = getId(item);
                  const extra = getExtra?.(item);
                  return (
                    <CommandItem key={itemId} value={String(itemId)} onSelect={() => escolher(item)}>
                      <Check className={cn("mr-2 h-4 w-4 shrink-0", value === itemId ? "opacity-100" : "opacity-0")} />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">{rotulo(item)}</span>
                        {extra && <span className="text-xs text-muted-foreground">{extra}</span>}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
